// Entrar sin contraseña: la persona escribe su correo y recibe un enlace de un solo uso.
// El secreto llega en el fragmento (#), que el navegador nunca envía al servidor ni en el
// Referer: se canjea por una cookie HttpOnly y se borra de la barra de direcciones.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { EnvelopeSimpleOpenIcon } from "@phosphor-icons/react";
import { escribir } from "@responia/api/cliente";
import { Button } from "@responia/ui/components/button";
import { Input } from "@responia/ui/components/input";
import { Label } from "@responia/ui/components/label";
import { Marca } from "@responia/ui/components/marca";
import { Card } from "@responia/ui/tremor/Card";
import { MensajeError } from "@/errores";
import { useT } from "@/useT";

type Paso = "correo" | "enviado" | "entrando";

export function Acceso() {
  const t = useT();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [paso, setPaso] = useState<Paso>(() => (window.location.hash.length > 20 ? "entrando" : "correo"));
  const [correo, setCorreo] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const canjeado = useRef(false);

  useEffect(() => {
    const token = window.location.hash.slice(1);
    if (token.length <= 20 || canjeado.current) return;
    canjeado.current = true;
    history.replaceState(null, "", window.location.pathname);
    escribir("/acceso/canjear", { token })
      .then(async () => { await qc.invalidateQueries(); navegar({ to: "/" }); })
      .catch((e) => { setError(e); setPaso("correo"); });
  }, [navegar, qc]);

  async function pedir(ev: FormEvent) {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await escribir("/acceso/enlace", { email: correo });
      setPaso("enviado");
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-10">
      <Card className="flex w-full max-w-md flex-col gap-6 p-8">
        <Marca className="h-8 w-auto self-start text-foreground" titulo={t("app.nombre")} />

        {paso === "entrando" && <p className="text-sm text-muted-foreground" aria-live="polite">{t("acceso.entrando")}</p>}

        {paso === "correo" && (
          <form onSubmit={pedir} className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl font-semibold text-foreground sm:text-2xl">{t("acceso.titulo")}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{t("acceso.subtitulo")}</p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="correo">{t("acceso.correo")}</Label>
              <Input id="correo" type="email" autoComplete="email" required value={correo}
                onChange={(e) => setCorreo(e.target.value)} />
            </div>
            {!!error && <MensajeError error={error} />}
            <Button type="submit" size="lg" disabled={enviando}>{t("acceso.enviar")}</Button>
          </form>
        )}

        {paso === "enviado" && (
          <div className="flex flex-col items-start gap-3" aria-live="polite">
            <EnvelopeSimpleOpenIcon className="size-6 text-prouve" />
            <h1 className="text-xl font-semibold text-foreground sm:text-2xl">{t("acceso.enviado.titulo")}</h1>
            <p className="text-sm text-muted-foreground">{t("acceso.enviado.texto", { correo })}</p>
            <Button variant="outline" onClick={() => setPaso("correo")}>{t("acceso.otroCorreo")}</Button>
          </div>
        )}
      </Card>
    </main>
  );
}
